import { useState } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import {
  PenLine,
  Copy,
  Check,
  Loader2,
  RefreshCw,
  MessageSquare,
  ArrowRight,
  XCircle,
  Calendar,
  FileText,
  List
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Intent options with icons
const INTENTS = [
  { id: 'reply', label: 'Reply', icon: MessageSquare, description: 'Direct reply' },
  { id: 'follow_up', label: 'Follow Up', icon: ArrowRight, description: 'Continue conversation' },
  { id: 'decline', label: 'Decline', icon: XCircle, description: 'Politely decline' },
  { id: 'schedule_call', label: 'Schedule', icon: Calendar, description: 'Arrange meeting' },
  { id: 'update', label: 'Update', icon: FileText, description: 'Share progress' },
  { id: 'summary', label: 'Summary', icon: List, description: 'Key points' }
];

// Variant/tone options
const VARIANTS = [
  { id: 'concise', label: 'Concise', description: 'Brief & direct' },
  { id: 'standard', label: 'Standard', description: 'Balanced' },
  { id: 'detailed', label: 'Detailed', description: 'Thorough' },
  { id: 'friendly', label: 'Friendly', description: 'Warm tone' },
  { id: 'firm', label: 'Firm', description: 'Assertive' },
  { id: 'diplomatic', label: 'Diplomatic', description: 'Tactful' }
];

const DraftModal = ({
  open,
  onOpenChange,
  sourceType, // "chat" | "email"
  sourceId,   // conversation_id or thread_id
  onUseDraft  // callback when user wants to use the draft
}) => {
  const [intent, setIntent] = useState('reply');
  const [variant, setVariant] = useState('standard');
  const [generating, setGenerating] = useState(false);
  const [draftText, setDraftText] = useState('');
  const [copied, setCopied] = useState(false);

  const generateDraft = async () => {
    setGenerating(true);
    setDraftText('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/drafts/generate`,
        {
          source_type: sourceType,
          source_id: sourceId,
          intent,
          variant,
          max_words: 150
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setDraftText(response.data.draft_text);
    } catch (error) {
      console.error('Failed to generate draft:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate draft');
    } finally {
      setGenerating(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(draftText);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy');
    }
  };

  const handleUseDraft = () => {
    if (onUseDraft) {
      onUseDraft(draftText);
    }
    onOpenChange(false);
  };

  const handleClose = () => {
    setDraftText('');
    setIntent('reply');
    setVariant('standard');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col" data-testid="draft-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PenLine className="h-5 w-5 text-blue-400" />
            Smart Draft
          </DialogTitle>
          <DialogDescription>
            Generate a draft {sourceType === 'email' ? 'email' : 'message'} with your preferred style
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2 flex-1 overflow-y-auto">
          {/* Intent Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">What do you want to say?</label>
            <div className="grid grid-cols-3 gap-2">
              {INTENTS.map(({ id, label, icon: Icon, description }) => (
                <button
                  key={id}
                  onClick={() => setIntent(id)}
                  className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-all ${
                    intent === id
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                  data-testid={`intent-${id}`}
                >
                  <Icon className={`h-5 w-5 ${intent === id ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className="text-sm font-medium">{label}</span>
                  <span className="text-xs text-muted-foreground">{description}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Variant/Tone Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-muted-foreground">How should it sound?</label>
            <div className="flex flex-wrap gap-2">
              {VARIANTS.map(({ id, label, description }) => (
                <button
                  key={id}
                  onClick={() => setVariant(id)}
                  className={`px-3 py-2 rounded-lg border transition-all ${
                    variant === id
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/50 hover:bg-muted/50'
                  }`}
                  data-testid={`variant-${id}`}
                >
                  <span className="text-sm font-medium">{label}</span>
                  <span className="text-xs text-muted-foreground ml-1">({description})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <Button
            onClick={generateDraft}
            disabled={generating}
            className="w-full gap-2"
            data-testid="generate-draft-btn"
          >
            {generating ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : draftText ? (
              <>
                <RefreshCw className="h-4 w-4" />
                Regenerate
              </>
            ) : (
              <>
                <PenLine className="h-4 w-4" />
                Generate Draft
              </>
            )}
          </Button>

          {/* Generated Draft */}
          {draftText && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-muted-foreground">Generated Draft</label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={copyToClipboard}
                  className="h-8 gap-1.5"
                >
                  {copied ? (
                    <>
                      <Check className="h-3.5 w-3.5 text-green-500" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-3.5 w-3.5" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <Textarea
                value={draftText}
                onChange={(e) => setDraftText(e.target.value)}
                className="min-h-[120px] resize-none"
                placeholder="Your draft will appear here..."
                data-testid="draft-textarea"
              />
              <p className="text-xs text-muted-foreground">
                You can edit the draft above before using it
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          {draftText && (
            <Button onClick={handleUseDraft} className="gap-2" data-testid="use-draft-btn">
              <Check className="h-4 w-4" />
              Use Draft
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DraftModal;
